import React, { useState, useEffect } from 'react';
import { LineChart, BarChart, ChartsGrid, ChartsYAxis, ChartsTooltip, ChartsLegend } from '@mui/x-charts';
import { Paper, CircularProgress, Box, Autocomplete, TextField, Typography, ToggleButtonGroup, ToggleButton, Tooltip, Chip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getYAxisConfig, formatValueByMode, SCALE_MODES, SCALE_MODE_LABELS, SCALE_MODE_TOOLTIPS } from '../../utils/chartScaleUtils';

const CHART_MARGIN = { left: 80, right: 20, top: 40, bottom: 70 };

const CategoryChart = ({ chartData, categoryData, hiddenCategories, colors, onRequestMoreData, onPeriodChange, loading = false, selectedYear, years = [], onYearChange, chartType = 'bar', numRecords: externalNumRecords, scaleMode = SCALE_MODES.ABSOLUTE, scaleMax, onScaleModeChange, outlierInfo = { outlierCategories: [] } }) => {
  const colorMap = Object.fromEntries(
    categoryData.map((cat, index) => [cat.category, colors[index % colors.length]])
  );
  const [numRecords, setNumRecords] = useState(externalNumRecords || 12);
  const [displayData, setDisplayData] = useState([]);

  useEffect(() => {
    updateDisplayData();
  }, [chartData, numRecords, hiddenCategories, scaleMode]);

  // Sync numRecords with external value when it changes
  useEffect(() => {
    if (externalNumRecords !== undefined) {
      setNumRecords(externalNumRecords);
      if (onPeriodChange) {
        onPeriodChange(externalNumRecords);
      }
      if (onRequestMoreData && externalNumRecords > chartData.length) {
        onRequestMoreData(externalNumRecords);
      }
    }
  }, [externalNumRecords]);

  // Update numRecords when chartData changes to reflect available data
  useEffect(() => {
    if (chartData.length > 0) {
      setNumRecords(prev => Math.min(prev, chartData.length));
    }
  }, [chartData.length]);


  const updateDisplayData = () => {
    const slicedData = chartData.slice(-numRecords);

    const dataWithTotal = slicedData.map(monthData => {
      const total = Object.keys(monthData)
        .filter(key => key !== 'month')
        .filter(key => !hiddenCategories.includes(key))
        .reduce((sum, category) => {
          return sum + (parseFloat(monthData[category]?.toString().replace(/[^\d.-]/g, '')) || 0);
        }, 0);

      return {
        ...monthData,
        'TOTAL': total
      };
    });

    setDisplayData(dataWithTotal);
  };

  const yAxisConfig = getYAxisConfig(scaleMode, scaleMax);

  const valueFormatter = (value) => formatValueByMode(value, scaleMode);

  const commonProps = {
    dataset: displayData,
    yAxis: [yAxisConfig],
    children: (
      <>
        <ChartsGrid />
        <ChartsYAxis />
        <ChartsTooltip />
        <ChartsLegend />
      </>
    ),
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {onYearChange && (
            <>
              <Typography variant="body2" color="text.secondary">
                Período:
              </Typography>
              <Autocomplete
                options={['All', ...years]}
                value={selectedYear}
                onChange={onYearChange}
                renderInput={(params) => (
                  <TextField {...params} label="Ano" variant="outlined" size="small" />
                )}
                sx={{ minWidth: 120 }}
                disableClearable
              />
            </>
          )}

          {onScaleModeChange && (
            <>
              <Box sx={{ borderLeft: 1, borderColor: 'divider', height: 24, mx: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Escala:
              </Typography>
              <ToggleButtonGroup
                value={scaleMode}
                exclusive
                onChange={onScaleModeChange}
                size="small"
              >
                {Object.entries(SCALE_MODE_LABELS).map(([mode, label]) => (
                  <Tooltip key={mode} title={SCALE_MODE_TOOLTIPS[mode]} arrow>
                    <ToggleButton value={mode}>
                      {label}
                    </ToggleButton>
                  </Tooltip>
                ))}
              </ToggleButtonGroup>

              {outlierInfo.outlierCategories.length > 0 && (
                <Tooltip
                  title={`Outliers detectados: ${outlierInfo.outlierCategories.join(', ')}`}
                  arrow
                >
                  <Chip
                    icon={<WarningAmberIcon />}
                    label={`${outlierInfo.outlierCategories.length} outlier${outlierInfo.outlierCategories.length > 1 ? 's' : ''}`}
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                </Tooltip>
              )}

              {scaleMode === SCALE_MODES.NORMALIZED && outlierInfo.outlierCategories.length > 0 && (
                <Chip
                  label={`Excluindo: ${outlierInfo.outlierCategories.join(', ')}`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
            </>
          )}

          {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
        </Box>
      </Paper>

      <Box sx={{ position: 'relative', minHeight: 500 }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                zIndex: 1,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {chartType === 'line' ? (
            <LineChart
              {...commonProps}
              xAxis={[{ dataKey: 'month', scaleType: 'point' }]}
              series={[
                ...categoryData
                  .filter(category => !hiddenCategories.includes(category.category))
                  .map((category) => ({
                    dataKey: category.category,
                    label: category.category,
                    stroke: colorMap[category.category],
                    strokeWidth: 3,
                    valueFormatter,
                  })),
                ...(scaleMode !== SCALE_MODES.PERCENTAGE ? [{
                  dataKey: 'TOTAL',
                  label: 'TOTAL',
                  stroke: '#000000',
                  strokeWidth: 4,
                  strokeDasharray: '5 5',
                  valueFormatter,
                }] : []),
              ]}
              style={{ width: '100%', height: 500 }}
              grid={{ vertical: true, horizontal: true }}
              margin={CHART_MARGIN}
            />
          ) : (
            <BarChart
              {...commonProps}
              xAxis={[{ dataKey: 'month', scaleType: 'band' }]}
              series={[
                ...categoryData
                  .filter(category => !hiddenCategories.includes(category.category))
                  .map((category) => ({
                    dataKey: category.category,
                    label: category.category,
                    color: colorMap[category.category],
                    stack: 'categories',
                    valueFormatter,
                  })),
                ...(scaleMode !== SCALE_MODES.PERCENTAGE ? [{
                  dataKey: 'TOTAL',
                  label: '📊 TOTAL',
                  color: 'transparent',
                  valueFormatter,
                }] : []),
              ]}
              style={{ width: '100%', height: 500 }}
              grid={{ vertical: true, horizontal: true }}
              margin={CHART_MARGIN}
            />
          )}
        </Box>
    </Box>
  );
};

export default CategoryChart;
